/**
 * Une ligne de la liste de jalons (maquette écran 2) :
 * pastille · nom + puce de revue · cible · replanifié · état · chevron.
 *
 * Le chevron n'apparaît que sur les jalons qui portent quelque chose à déplier —
 * une revue formelle, ou une checklist héritée (LaunchPad/UnLock d'avant le PAD,
 * qui serait sinon invisible et donc perdue).
 */
import { Box, Chip, Collapse, IconButton, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { Milestone } from '@altyor-kronos/shared-types';
import { useTranslation } from 'react-i18next';
import { colors } from '../../core/theme/theme';
import { milestoneCompliance, COMPLIANCE_COLOR, type ComplianceState } from './compliance';

/**
 * ponytail: `dd/MM/yyyy` en UTC, pas `toLocaleDateString()`. Les dates de jalon
 * sont des dates-calendaires stockées à minuit UTC : les rendre dans le fuseau
 * du navigateur affiche la veille dès qu'il est négatif. Passer par Intl le jour
 * où l'app doit vraiment localiser ses formats de date.
 */
export function formatDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}

/** Couleur de la pastille de gauche — reprend le mapping unique de compliance.ts. */
const DOT_COLOR: Record<ComplianceState, string> = {
  done: colors.bodyText,
  upcoming: colors.lightGray,
  ok: colors.yellow,
  acceptable: colors.yellow,
  nok: colors.red,
};

/** Grille partagée par l'en-tête et les lignes — une seule définition. */
export const ROW_GRID = {
  display: 'grid',
  gridTemplateColumns: { xs: '20px 1fr 40px', md: '20px 1fr 100px 100px 140px 40px' },
  gap: 1.5,
  alignItems: 'center',
  px: 1.75,
  py: 1.25,
  borderBottom: `1px solid ${colors.lightGray}`,
} as const;

export interface MilestoneRowProps {
  milestoneId: string;
  milestone: Milestone;
  now: Date;
  expanded: boolean;
  onToggle: () => void;
  /** Panneau déplié — fourni par le parent (panneau de revue ou checklist héritée). */
  children?: React.ReactNode;
  /** Action de dernière colonne pour un jalon sans panneau (franchir / rouvrir). */
  action?: React.ReactNode;
}

export default function MilestoneRow({
  milestoneId,
  milestone,
  now,
  expanded,
  onToggle,
  children,
  action,
}: MilestoneRowProps) {
  const { t } = useTranslation();
  const c = milestoneCompliance(milestone, now);
  const expandable = Boolean(children);

  const stateLabel =
    c.state === 'done'
      ? t('projects.milestonesTab.statePassed')
      : c.state === 'upcoming'
      ? t('projects.milestonesTab.stateUpcoming')
      : `${t(`projects.milestonesTab.state_${c.state}`)} · +${c.daysLate} j`;

  return (
    <Box>
      <Box
        data-testid={`milestone-row-${milestoneId}`}
        data-state={c.state}
        sx={{
          ...ROW_GRID,
          fontSize: 13,
          backgroundColor: expanded
            ? colors.peach
            : c.state === 'nok'
            ? '#FFF3F1'
            : 'transparent',
          '&:last-of-type': { borderBottom: 0 },
        }}
      >
        <Box
          sx={{
            width: 9,
            height: 9,
            justifySelf: 'center',
            backgroundColor: DOT_COLOR[c.state],
            outline: c.state === 'ok' ? `1px solid ${colors.bodyText}` : undefined,
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {milestone.name}
          </Typography>
          {milestone.reviewName && (
            <Chip
              size="small"
              label={milestone.reviewName}
              data-testid={`milestone-review-flag-${milestoneId}`}
              sx={{ backgroundColor: colors.warmBeige, fontSize: 10 }}
            />
          )}
        </Box>

        {/* Cible / replanifié — masqués sous md, l'état porte déjà le retard. */}
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={`milestone-target-${milestoneId}`}
          sx={{ display: { xs: 'none', md: 'block' }, fontVariantNumeric: 'tabular-nums' }}
        >
          {milestone.targetDate ? formatDay(milestone.targetDate.toDate()) : '—'}
        </Typography>
        <Typography
          variant="caption"
          data-testid={`milestone-rescheduled-${milestoneId}`}
          sx={{
            display: { xs: 'none', md: 'block' },
            fontVariantNumeric: 'tabular-nums',
            color: milestone.rescheduledDate ? colors.bodyText : '#C9C4BC',
          }}
        >
          {milestone.rescheduledDate ? formatDay(milestone.rescheduledDate.toDate()) : '—'}
        </Typography>

        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Chip
            size="small"
            label={stateLabel}
            color={COMPLIANCE_COLOR[c.state]}
            data-testid={`milestone-state-${milestoneId}`}
          />
        </Box>

        {expandable ? (
          <IconButton
            size="small"
            onClick={onToggle}
            data-testid={`milestone-expand-${milestoneId}`}
            aria-expanded={expanded}
            aria-label={t('projects.milestonesTab.toggleReview')}
            sx={{ justifySelf: 'center' }}
          >
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ justifySelf: 'center' }}>{action}</Box>
        )}
      </Box>

      {expandable && (
        <Collapse in={expanded} unmountOnExit>
          {children}
        </Collapse>
      )}
    </Box>
  );
}
